/**
 * Deterministic fixture ChatListPort for S012 composition.
 *
 * Every factory invocation owns its request counter, pending-request set, and
 * abort tracking. Canonical seed literals are shared only as immutable source
 * values; every result returns freshly cloned and deeply frozen records.
 */
import { createConversationSummary, type ConversationSummary } from '../model';
import type { ChatListLoadResult, ChatListPort } from '../port';

export const FIXTURE_REFERENCE_TIME = '2026-07-22T20:00:00+03:00';
export const FIXTURE_LATENCY_MS = 40;

export interface FixtureLatency {
  readonly wait: () => Promise<void>;
}

export function createTimerFixtureLatency(milliseconds: number): FixtureLatency {
  if (!Number.isFinite(milliseconds) || milliseconds < 0) {
    throw new RangeError('fixture latency must be a non-negative finite number');
  }
  return Object.freeze({
    wait: () => new Promise<void>((resolve) => {
      window.setTimeout(resolve, milliseconds);
    }),
  });
}

export const immediateFixtureLatency: FixtureLatency = Object.freeze({
  wait: () => Promise.resolve(),
});

interface FixtureOptions {
  readonly latency?: FixtureLatency;
  readonly resultType?: 'loaded' | 'inactive_sponsor';
  readonly conversations?: readonly ConversationSummary[];
}

const CANONICAL_SEED: readonly ConversationSummary[] = Object.freeze([
  {
    channelId: 'channel-alpha',
    participant: {
      id: 'participant-anna',
      displayName: 'Анна',
      age: 27,
      city: 'Москва',
      avatar: {
        initials: 'А',
        backgroundColor: '#47d9bf',
        sourceNodeId: '38:2743',
      },
      isOnline: true,
    },
    lastMessage: 'Привет! Как дела? 😊',
    lastActivityAt: '2026-07-22T18:42:00+03:00',
    unreadCount: 2,
  },
  {
    channelId: 'channel-beta',
    participant: {
      id: 'participant-olga',
      displayName: 'Ольга',
      age: 29,
      city: 'Санкт-Петербург',
      avatar: {
        initials: 'О',
        backgroundColor: '#f5a0a0',
        sourceNodeId: '38:2759',
      },
      isOnline: false,
    },
    lastMessage: 'Договорились 👍',
    lastActivityAt: '2026-07-21T19:10:00+03:00',
    unreadCount: 0,
  },
  {
    channelId: 'channel-gamma',
    participant: {
      id: 'participant-maria',
      displayName: 'Мария',
      age: 25,
      city: 'Екатеринбург',
      avatar: {
        initials: 'М',
        backgroundColor: '#8fa7f5',
        sourceNodeId: '38:2770',
      },
      isOnline: false,
    },
    lastMessage: 'Хорошо, напишу завтра',
    lastActivityAt: '2026-07-21T09:30:00+03:00',
    unreadCount: 0,
  },
  {
    channelId: 'channel-delta',
    participant: {
      id: 'participant-elena',
      displayName: 'Елена',
      age: 31,
      city: 'Казань',
      avatar: {
        initials: 'Е',
        backgroundColor: '#a0cbe8',
        sourceNodeId: '38:2782',
      },
      isOnline: true,
    },
    lastMessage: 'Спасибо за вечер! ✨',
    lastActivityAt: '2026-07-20T21:00:00+03:00',
    unreadCount: 1,
  },
]);

function abortError(): DOMException {
  return new DOMException('The chat-list request was aborted.', 'AbortError');
}

function waitForLatency(latency: FixtureLatency, signal: AbortSignal): Promise<void> {
  if (signal.aborted) {
    return Promise.reject(abortError());
  }

  return new Promise<void>((resolve, reject) => {
    let settled = false;
    const finish = (result: 'resolve' | 'reject', error?: unknown) => {
      if (settled) return;
      settled = true;
      signal.removeEventListener('abort', onAbort);
      if (result === 'resolve') resolve();
      else reject(error);
    };
    const onAbort = () => finish('reject', abortError());
    signal.addEventListener('abort', onAbort, { once: true });

    Promise.resolve()
      .then(() => latency.wait())
      .then(
        () => finish(signal.aborted ? 'reject' : 'resolve', abortError()),
        (error) => finish('reject', error),
      );
  });
}

function snapshot(
  source: readonly ConversationSummary[],
): readonly ConversationSummary[] {
  return Object.freeze(source.map(createConversationSummary));
}

/**
 * S013 deterministic empty composition.
 *
 * Returns a fresh port whose `loadConversations` resolves to a loaded empty
 * result. The reducer normalizes a loaded empty list to `status: 'empty'`.
 * No special `empty` port-result variant is introduced; cancellation
 * behaviour and isolation guarantees are inherited from the underlying
 * factory. The canonical S012 seed is never mutated.
 */
export function createEmptyFixtureChatListPort(): ChatListPort {
  return createFixtureChatListPort({
    conversations: Object.freeze([] as const),
  });
}

export function createFixtureChatListPort(
  options: FixtureOptions = {},
): ChatListPort {
  const latency = options.latency
    ?? createTimerFixtureLatency(FIXTURE_LATENCY_MS);
  const resultType = options.resultType ?? 'loaded';
  const source = options.conversations ?? CANONICAL_SEED;

  // Deliberately per-instance. These values are never shared across factories.
  let callCounter = 0;
  const pendingRequests = new Set<number>();
  const abortedSignals = new WeakSet<AbortSignal>();

  return Object.freeze({
    async loadConversations(signal: AbortSignal): Promise<ChatListLoadResult> {
      callCounter += 1;
      const requestId = callCounter;
      pendingRequests.add(requestId);

      if (signal.aborted) {
        abortedSignals.add(signal);
        pendingRequests.delete(requestId);
        throw abortError();
      }

      try {
        await waitForLatency(latency, signal);
        if (signal.aborted) {
          abortedSignals.add(signal);
          throw abortError();
        }

        if (resultType === 'inactive_sponsor') {
          return Object.freeze({ type: 'inactive_sponsor' });
        }
        return Object.freeze({
          type: 'loaded',
          conversations: snapshot(source),
        });
      } finally {
        if (signal.aborted) {
          abortedSignals.add(signal);
        }
        pendingRequests.delete(requestId);
      }
    },
  });
}
